import { Component, Injector, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, Channel, ChannelService, ConfirmationComponent, User, UserService, VoipExtension } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { ChannelVoipRamalFormComponent } from './ramal-form/channel-voip-ramal-form.component';

@Component({
    selector: 'channel-voip-ramais-component',
    templateUrl: './channel-voip-ramais.component.html',
    styleUrls: ['./channel-voip-ramais.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelVoipRamaisComponent extends AbstractComponent implements OnInit {

  @Input('channel') channel: Channel;

  ramais: Array<VoipExtension> = [];
  users: Array<User> = [];

  constructor(
    injector: Injector,
    private readonly channelService: ChannelService,
    private readonly userService: UserService,
    private readonly dialog: MatDialog,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getUsers();
    this.getRamais();
  }

  getUsers(): void {
    this.userService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users: Array<User>) => this.users = users ?? [],
      });
  }

  getRamais(): void {
    if (!this.channel?._id) {
      return;
    }
    this.loadingContent = true;
    this.channelService.listExtensions(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ramais: Array<VoipExtension>) => {
          this.ramais = ramais ?? [];
          this.loadingContent = false;
        },
        error: (err) => {
          this.loadingContent = false;
          this.alertService.error(err?.error?.message);
        },
      });
  }

  get defaultExtension(): string | undefined {
    return this.channel?.metadata?.defaultExtension;
  }

  newRamal(): void {
    this.openForm();
  }

  editRamal(ramal: VoipExtension): void {
    this.openForm(ramal);
  }

  setAsDefault(ramal: VoipExtension): void {
    if (!this.channel?._id || !ramal?.sipUsername) {
      return;
    }
    this.channelService.setDefaultExtension(this.channel._id, ramal.sipUsername)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated: Channel) => {
          this.channel = { ...this.channel, metadata: updated?.metadata ?? this.channel?.metadata };
          this.alertService.success(this.t('channel.voipRamais.inboundSetSuccess'));
        },
        error: (err) => this.alertService.error(err?.error?.message),
      });
  }

  resync(ramal: VoipExtension): void {
    if (!this.channel?._id || !ramal?.sipUsername) {
      return;
    }
    this.channelService.resyncExtension(this.channel._id, ramal.sipUsername)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.alertService.success(this.t('channel.voipRamais.resyncSuccess'));
          this.getRamais();
        },
        error: (err) => this.alertService.error(err?.error?.message),
      });
  }

  private openForm(ramal?: VoipExtension): void {
    const dialogRef = this.dialog.open(ChannelVoipRamalFormComponent, {
      width: '600px',
      data: { channel: this.channel, users: this.users, ramal },
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.getRamais();
      }
    });
  }

  remove(ramal: VoipExtension): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, { width: '500px' });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (Boolean(result) === true) {
        this.channelService.removeExtension(this.channel._id, ramal.sipUsername)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.alertService.success(this.t('channel.voipRamais.ramalRemovedSuccess'));
              this.getRamais();
            },
            error: (err) => this.alertService.error(err?.error?.message),
          });
      }
    });
  }
}
